import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { api, pickLocale, formatApiErrorDetail } from '@/lib/api';

const CATS = ['elderly-care', 'animal-welfare', 'cow-care', 'healthcare', 'education', 'environment', 'community', 'disaster-relief'];

const emptyProject = () => ({
  slug: '',
  category: 'elderly-care',
  title: { en: '', hi: '', mr: '', gu: '' },
  summary: { en: '', hi: '', mr: '', gu: '' },
  description: { en: '', hi: '', mr: '', gu: '' },
  image_url: '',
  location: '',
  beneficiaries: 0,
  funds_raised: 0,
  funds_goal: 0,
  is_published: true,
});

export default function AdminProjects() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ['admin-projects'], queryFn: async () => (await api.get('/projects')).data });
  const [editing, setEditing] = useState(null); // null | 'new' | project_id
  const [form, setForm] = useState(emptyProject());

  const createMut = useMutation({
    mutationFn: async (data) => (await api.post('/admin/projects', data)).data,
    onSuccess: () => { toast.success('Project created'); qc.invalidateQueries(['admin-projects']); qc.invalidateQueries(['projects']); setEditing(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, data }) => (await api.put(`/admin/projects/${id}`, data)).data,
    onSuccess: () => { toast.success('Project updated'); qc.invalidateQueries(['admin-projects']); qc.invalidateQueries(['projects']); setEditing(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/projects/${id}`)).data,
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-projects']); qc.invalidateQueries(['projects']); },
  });

  const startNew = () => { setForm(emptyProject()); setEditing('new'); };
  const startEdit = (p) => { setForm({ ...p, title: p.title || {en:'',hi:'',mr:'',gu:''}, summary: p.summary || {en:'',hi:'',mr:'',gu:''}, description: p.description || {en:'',hi:'',mr:'',gu:''} }); setEditing(p.id); };
  const cancel = () => setEditing(null);

  const setLoc = (field, lang) => (e) => setForm(f => ({ ...f, [field]: { ...f[field], [lang]: e.target.value } }));
  const setVal = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setNum = (field) => (e) => setForm(f => ({ ...f, [field]: Number(e.target.value) || 0 }));

  const save = () => {
    const payload = { ...form };
    if (editing === 'new') createMut.mutate(payload);
    else updateMut.mutate({ id: editing, data: payload });
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-ukgm-blue font-semibold">Manage Projects</h1>
        <button onClick={startNew} className="inline-flex items-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-5 py-2.5 rounded-full hover:bg-ukgm-goldDark" data-testid="new-project-btn">
          <Plus className="w-4 h-4" /> New Project
        </button>
      </div>

      {editing && (
        <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 p-6 space-y-4" data-testid="project-editor">
          <div className="flex items-center justify-between">
            <div className="font-serif text-lg text-ukgm-blue font-semibold">{editing === 'new' ? 'New Project' : 'Edit Project'}</div>
            <button onClick={cancel}><X className="w-5 h-5 text-ukgm-slate" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input placeholder="Slug (unique)" value={form.slug} onChange={setVal('slug')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-slug" />
            <select value={form.category} onChange={setVal('category')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-category">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Image URL" value={form.image_url} onChange={setVal('image_url')} className="md:col-span-2 px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-image" />
            <input placeholder="Location" value={form.location} onChange={setVal('location')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-location" />
            <input type="number" placeholder="Beneficiaries" value={form.beneficiaries} onChange={setNum('beneficiaries')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-beneficiaries" />
            <input type="number" placeholder="Funds raised (₹)" value={form.funds_raised} onChange={setNum('funds_raised')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-funds-raised" />
            <input type="number" placeholder="Funds goal (₹)" value={form.funds_goal} onChange={setNum('funds_goal')} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="edit-funds-goal" />
          </div>
          {['title', 'summary', 'description'].map(field => (
            <div key={field}>
              <div className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{field}</div>
              <div className="mt-2 grid md:grid-cols-2 gap-3">
                {['en', 'hi', 'mr', 'gu'].map(lg => (
                  field === 'description' ? (
                    <textarea key={lg} placeholder={`${lg.toUpperCase()}`} rows={2} value={form[field]?.[lg] || ''} onChange={setLoc(field, lg)} className="px-4 py-2.5 rounded-xl bg-ukgm-cream text-sm resize-none" data-testid={`edit-${field}-${lg}`} />
                  ) : (
                    <input key={lg} placeholder={lg.toUpperCase()} value={form[field]?.[lg] || ''} onChange={setLoc(field, lg)} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid={`edit-${field}-${lg}`} />
                  )
                ))}
              </div>
            </div>
          ))}
          <button onClick={save} disabled={createMut.isPending || updateMut.isPending} className="inline-flex items-center gap-2 bg-ukgm-blue text-white font-bold px-5 py-2.5 rounded-full hover:bg-ukgm-blueDark disabled:opacity-50" data-testid="save-project-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      )}

      <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ukgm-cream">
            <tr className="text-left text-xs uppercase tracking-widest text-ukgm-slate">
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Beneficiaries</th>
              <th className="px-5 py-3">Raised</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-ukgm-gold/15" data-testid={`admin-project-row-${p.slug}`}>
                <td className="px-5 py-3 text-ukgm-charcoal font-medium">{pickLocale(p.title, 'en')}</td>
                <td className="px-5 py-3 text-ukgm-slate">{p.category}</td>
                <td className="px-5 py-3 text-ukgm-slate">{p.beneficiaries?.toLocaleString('en-IN')}</td>
                <td className="px-5 py-3 text-ukgm-blue font-semibold">₹{Math.round(p.funds_raised / 100000)}L</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => startEdit(p)} className="text-ukgm-blue hover:text-ukgm-gold text-xs font-bold mr-3">EDIT</button>
                  <button onClick={() => window.confirm('Delete?') && deleteMut.mutate(p.id)} className="text-red-600 hover:text-red-700 text-xs font-bold inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> DEL</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
